import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import fr from './resources/fr.json';
import en from './resources/en.json';

// PAR-15 — fondation i18n de `web-parent`, la SEULE des deux apps concernée
// (web-admin reste hors périmètre, décision explicite du ticket). Bibliothèque
// : react-i18next (Étape 0, validée par le PO) — API par hook, interpolation/
// pluralisation natives, aucune raison de réinventer une solution maison sur
// un périmètre qui va croître au-delà de cette seule page.
//
// Préférence de langue stockée PAR APPAREIL (`localStorage`), jamais par
// compte — décision Étape 0 validée : zéro migration Prisma, réversible,
// suffisant pour cette fondation. Le français reste le défaut pour TOUT
// compte existant, l'anglais est un choix EXPLICITE — AUCUNE détection
// automatique de la langue du navigateur ici (contrairement au comportement
// usuel d'un plugin de détection i18next), pour ne jamais imposer un
// changement de langue non demandé.
export const LOCALE_STORAGE_KEY = 'monecolier.web-parent.locale';

export type SupportedLocale = 'fr' | 'en';
export const SUPPORTED_LOCALES: SupportedLocale[] = ['fr', 'en'];
const DEFAULT_LOCALE: SupportedLocale = 'fr';

function isSupportedLocale(value: string | null): value is SupportedLocale {
  return value === 'fr' || value === 'en';
}

export function readStoredLocale(): SupportedLocale {
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    return isSupportedLocale(stored) ? stored : DEFAULT_LOCALE;
  } catch {
    // Stockage indisponible (mode privé strict, etc.) — retombe sur le
    // défaut, jamais une erreur qui bloquerait le rendu de l'app.
    return DEFAULT_LOCALE;
  }
}

/**
 * Synchronise `<html lang>` avec la langue active (audit Impeccable,
 * 2026-09-07).
 *
 * PROBLÈME RÉEL : `index.html` déclare `lang="fr"` en dur, alors que cette app
 * est bilingue. En anglais, une synthèse vocale lisait donc du contenu anglais
 * avec la prononciation française, et les correcteurs orthographiques /
 * moteurs de traduction se trompaient de langue (WCAG 2.1 A, 3.1.1 Language of
 * Page).
 *
 * Appelé à l'initialisation ET à chaque changement de langue : le premier
 * couvre le parent dont la préférence anglaise est déjà en `localStorage` (il
 * ne repasse pas par `setLocale` au chargement suivant), le second le
 * changement en cours de session.
 *
 * `document` est gardé par un test d'existence : ce module est importé pour
 * effet de bord par `main.tsx` mais aussi chargé par des tests, et une
 * exception ici casserait l'initialisation i18n avant tout rendu.
 */
function syncDocumentLang(locale: SupportedLocale): void {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
  }
}

const initialLocale = readStoredLocale();
syncDocumentLang(initialLocale);

void i18next.use(initReactI18next).init({
  resources: {
    fr: { translation: fr },
    en: { translation: en },
  },
  lng: initialLocale,
  fallbackLng: DEFAULT_LOCALE,
  interpolation: {
    // React échappe déjà tout ce qu'il rend — l'échappement supplémentaire
    // par défaut d'i18next casserait nos propres apostrophes/accents sans
    // aucun bénéfice de sécurité ici (recommandation standard react-i18next).
    escapeValue: false,
  },
});

export function setLocale(locale: SupportedLocale): void {
  void i18next.changeLanguage(locale);
  syncDocumentLang(locale);
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Best-effort — un stockage indisponible ne doit jamais empêcher le
    // changement de langue de la session en cours.
  }
}

export { i18next };
